// =============================================================================
// CTA BUTTON — Primary Call-to-Action Component
// =============================================================================
// The main conversion button used throughout the site.
// "Reserve a Table", "Order Online", "View Menu" — these are the actions
// that generate revenue, so they need to be visually prominent.
//
// This component wraps the base .btn styles from base.css with:
// - Proper semantic HTML (<a> for links, <button> for actions)
// - Accessibility attributes (aria-label for icon-only buttons)
// - Optional micro-animation on hover (translateY + shadow)
// =============================================================================

import type { ReactNode, ButtonHTMLAttributes, AnchorHTMLAttributes } from 'react';

/** Props shared by both button and link variants */
interface CTABaseProps {
  /** Button visual style */
  variant?: 'primary' | 'secondary' | 'ghost';
  /** Button size */
  size?: 'sm' | 'md' | 'lg';
  /** Content inside the button (text, icon, etc.) */
  children: ReactNode;
  /** Optional CSS class additions */
  className?: string;
  /** Optional icon before the text */
  icon?: ReactNode;
}

/** Props when CTA is a <button> element (for actions) */
interface CTAButtonProps
  extends CTABaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className' | 'children'> {
  /** Render as a <button> element */
  as?: 'button';
  /** No href for buttons */
  href?: never;
}

/** Props when CTA is an <a> element (for navigation/links) */
interface CTALinkProps
  extends CTABaseProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className' | 'children'> {
  /** Render as an <a> element */
  as: 'link';
  /** URL to navigate to */
  href: string;
}

type CTAButtonComponentProps = CTAButtonProps | CTALinkProps;

/**
 * Primary call-to-action button/link.
 *
 * USAGE:
 *   <CTAButton variant="primary" onClick={handleReserve}>
 *     Reserve a Table
 *   </CTAButton>
 *
 *   <CTAButton as="link" href="/menu" variant="secondary">
 *     View Menu
 *   </CTAButton>
 */
export function CTAButton({
  variant = 'primary',
  size = 'md',
  children,
  className = '',
  icon,
  ...props
}: CTAButtonComponentProps) {
  // Build the CSS class string
  const sizeClass = size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '';
  const classes = `btn btn-${variant} ${sizeClass} ${className}`.trim();

  // Content with optional icon
  const content = (
    <>
      {icon && (
        <span className="btn-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      {children}
    </>
  );

  // Render as <a> or <button> based on 'as' prop
  if (props.as === 'link') {
    const linkProps = { ...(props as CTALinkProps) };
    delete (linkProps as Record<string, unknown>).as;
    return (
      <a className={classes} {...linkProps}>
        {content}
      </a>
    );
  }

  const buttonProps = { ...(props as CTAButtonProps) };
  delete (buttonProps as Record<string, unknown>).as;
  return (
    <button className={classes} type="button" {...buttonProps}>
      {content}
    </button>
  );
}
